import { createContext, useCallback, useContext, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { request } from "@/Api";
import {
    selectActiveOrganization,
    selectNavTokenContext,
    selectOrganizations,
    setToken,
} from "@/auth/authSlice";
import useEntitlements from "@/auth/useEntitlements";
import {
    getMenuItemsExternal,
    getMenuItemsMain,
    getMenuItemsSupport,
    getMenuItemsUser,
    getMenus,
} from "@/nav/menuItems";
import {
    getCurrentRouteBase,
    useHandlePageGoTo,
    useIsHidden,
} from "@/nav/navHooks";
import { selectNavOpen, setNavOpen } from "@/nav/navSlice";

const NavContext = createContext(null);

function NavProvider({ children }) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const open = useSelector(selectNavOpen);
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));
    const isHidden = useIsHidden();
    const token = useSelector(selectNavTokenContext);
    const activeOrganization = useSelector(selectActiveOrganization);
    const organizations = useSelector(selectOrganizations);
    const { hasFeature } = useEntitlements();
    const handlePageGoTo = useHandlePageGoTo();

    const {
        menuItemsExternal,
        menuItemsMain,
        menuItemsSupport,
        menuItemsUser,
        menus,
    } = useMemo(
        () => ({
            menuItemsExternal: getMenuItemsExternal(token, hasFeature),
            menuItemsMain: getMenuItemsMain(token, hasFeature),
            menuItemsSupport: getMenuItemsSupport(token, hasFeature),
            menuItemsUser: getMenuItemsUser(token, hasFeature),
            menus: getMenus(token, hasFeature),
        }),
        [hasFeature, token],
    );
    const currentRoute = useMemo(
        () => getCurrentRouteBase(location.pathname),
        [location.pathname],
    );

    const commitNavOpen = useCallback(
        (nextOpen) => {
            localStorage.setItem("navOpen", nextOpen);
            dispatch(setNavOpen(nextOpen));
        },
        [dispatch],
    );

    const closeNav = useCallback(() => {
        commitNavOpen(false);
    }, [commitNavOpen]);

    const activateItem = useCallback(
        (item, options = {}) => {
            if (item.action) {
                dispatch(item.action());
            } else if (item.url) {
                handlePageGoTo(item.url);
            }

            if (options.closeOnNavigate) {
                closeNav();
            }
        },
        [closeNav, dispatch, handlePageGoTo],
    );

    const selectOrganization = useCallback(
        (organization) => {
            if (!organization || organization.paused) {
                return;
            }

            request
                .post(`enter-organization/${organization.id}`)
                .then((response) => {
                    dispatch(setToken(response.data));
                    navigate("dashboard", { state: organization.id });
                });
        },
        [dispatch, navigate],
    );

    // This provider is the navigation contract: screens compose UI pieces while
    // entitlement filtering, route state, and workspace switching stay in one place.
    const value = useMemo(
        () => ({
            actions: {
                activateItem,
                closeNav,
                commitNavOpen,
                selectOrganization,
            },
            state: {
                activeOrganization,
                currentRoute,
                isHidden,
                isSmall,
                menuItemsExternal,
                menuItemsMain,
                menuItemsSupport,
                menuItemsUser,
                menus,
                open,
                organizations,
            },
        }),
        [
            activateItem,
            activeOrganization,
            closeNav,
            commitNavOpen,
            currentRoute,
            isHidden,
            isSmall,
            menuItemsExternal,
            menuItemsMain,
            menuItemsSupport,
            menuItemsUser,
            menus,
            open,
            organizations,
            selectOrganization,
        ],
    );

    return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

function useNavContext() {
    const context = useContext(NavContext);

    if (!context) {
        throw new Error("useNavContext must be used within NavProvider");
    }

    return context;
}

export { NavProvider, useNavContext };
